import {
  Button,
  Col,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  getCompanies,
  getCompanyPolicies,
  getCompanyPolicyAcknowledgments,
  sendCompanyPolicyReminder,
} from "../repository/data.repository";
import {
  CompanyPolicy,
  CompanyPolicyAcknowledgment,
} from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import {
  createScopedSearchRequest,
  createSearchRequest,
} from "../hooks/useSearchRequest";
import RemoteSelect from "../components/analytics/RemoteSelect";
import { getProblemMessage } from "../utils/http";

// PD-01 §6 — Policy Acknowledgment Monitoring. Picks a policy, lists every
// acknowledgment on it, and lets the admin fire a reminder to any employee
// of the policy's company who hasn't acknowledged yet.
function PolicyAcknowledgmentsPage() {
  const [policyUid, setPolicyUid] = useState<string | undefined>();
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [items, setItems] = useState<CompanyPolicyAcknowledgment[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const policiesRequest = createSearchRequest(getCompanyPolicies, "name");
  const companiesRequest = createSearchRequest(getCompanies, "name");

  const load = useCallback(() => {
    if (!policyUid) {
      setItems([]);
      return;
    }
    setLoading(true);
    const conditions = [
      { field: "policyUid", values: [policyUid], operation: QueryOperation.Equal },
    ];
    if (companyUid) {
      conditions.push({
        field: "companyUid",
        values: [companyUid],
        operation: QueryOperation.Equal,
      });
    }
    getCompanyPolicyAcknowledgments({
      searchQuery: { conditions },
      pageNumber: 0,
      pageSize: 500,
      sortBy: "acknowledgedAt",
      sortDescending: true,
    })
      .then(({ data }) => setItems(data))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load acknowledgments."),
      )
      .finally(() => setLoading(false));
  }, [policyUid, companyUid]);

  useEffect(() => {
    load();
  }, [load]);

  function onSendReminder() {
    if (!policyUid) return;
    setSending(true);
    sendCompanyPolicyReminder(policyUid, companyUid ? { } : undefined)
      .then((count) => {
        message.success(
          count === 0
            ? "Everyone has already acknowledged this policy."
            : `Reminders queued for ${count} user${count === 1 ? "" : "s"}.`,
        );
      })
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to send reminders."),
      )
      .finally(() => setSending(false));
  }

  const columns: ColumnsType<CompanyPolicyAcknowledgment> = [
    {
      title: "User",
      dataIndex: "userUserName",
      render: (v: string, row) => {
        const name = [row.userFirstName, row.userLastName].filter(Boolean).join(" ");
        return name ? `${name} (${v})` : v;
      },
    },
    { title: "Company", dataIndex: "companyName" },
    { title: "Department", dataIndex: "departmentName" },
    {
      title: "Acknowledged At",
      dataIndex: "acknowledgedAt",
      render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => dayjs(a.acknowledgedAt).unix() - dayjs(b.acknowledgedAt).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: "Device",
      dataIndex: "deviceInfo",
      render: (v?: string) => (v ? <Tag>{v.slice(0, 40)}{v.length > 40 ? "…" : ""}</Tag> : "—"),
    },
    { title: "IP", dataIndex: "ipAddress", width: 140 },
  ];

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Policy Acknowledgments
        </Typography.Title>
      </Row>
      <Col className="flex flex-auto flex-col gap-4 overflow-hidden p-4 pt-0">
        <Row align="middle" gutter={12}>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Policy
            </Typography.Text>
            <RemoteSelect
              className="w-80"
              placeholder="Pick a policy to monitor"
              value={policyUid}
              onChange={setPolicyUid}
              request={policiesRequest}
            />
          </Col>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Company
            </Typography.Text>
            <RemoteSelect
              className="w-64"
              placeholder="All companies"
              value={companyUid}
              onChange={setCompanyUid}
              request={companiesRequest}
            />
          </Col>
          <Col flex="auto" />
          <Col>
            <Space>
              <Button
                type="primary"
                disabled={!policyUid}
                loading={sending}
                onClick={onSendReminder}
              >
                Send Reminder
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          rowKey="uid"
          size="small"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          locale={{
            emptyText: policyUid
              ? "No acknowledgments on this policy yet."
              : "Pick a policy above to see its acknowledgment audit.",
          }}
        />
      </Col>
    </Col>
  );
}

export default PolicyAcknowledgmentsPage;
